import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, _ } from '@ngx-translate/core';
import Decimal from 'decimal.js';
import { LoaderComponent } from 'src/app/components/loader/loader.component';
import { PaginationComponent } from 'src/app/components/pagination/pagination.component';
import { PriceComponent } from 'src/app/components/price/price.component';
import { LoadingUtils } from 'src/app/utils/loading.utils';
import { AlertUtils } from 'src/app/utils/alert.utils';
import { Paginated } from '../../../../models/papginated';
import { Card } from '../../../../models/transaction';
import { User } from '../../../../models/user';
import { WalletBalance, WalletTransaction } from '../../../../models/wallet';
import { AuthService } from '../../../../services/auth.service';
import { CardService } from '../../../../services/card.service';
import { ToastService } from '../../../../services/toast.service';
import { ValidationScopeWarningService } from '../../../../services/validation-scope-warning.service';
import { WalletService } from '../../../../services/wallet.service';
import { IbanValidator } from '../../../../validators/iban.validator';

@Component({
    selector: 'app-profile-tab-wallet',
    templateUrl: './profile-tab-wallet.component.html',
    styleUrls: ['./profile-tab-wallet.component.scss'],
    imports: [
        CommonModule,
        PriceComponent,
        TranslatePipe,
        LoaderComponent,
        FormsModule,
        ReactiveFormsModule,
        DatePipe,
        PaginationComponent,
    ],
})
export class ProfileTabWalletComponent implements OnInit {
    cards: Card[];
    user: User;
    loadingIban: boolean = true;
    loadingWallet: boolean = true;
    loadingTransactions: boolean = true;
    wallet: WalletBalance = new WalletBalance();
    transactions: Paginated<WalletTransaction> = { items: [], total: 0, page: 1, perPage: 10 };
    withdrawAmount: string = '';
    ibanForm = new FormGroup({
        iban: new FormControl('', [Validators.required, IbanValidator]),
    });

    constructor(
        private readonly authService: AuthService,
        private readonly cardService: CardService,
        private readonly walletService: WalletService,
        private readonly toastService: ToastService,
        private readonly validationScopeWarningService: ValidationScopeWarningService,
    ) {}

    ngOnInit() {
        this.authService.userLoggedIn.subscribe((u) => {
            this.user = u;
        });
        this.loadCards();
        this.loadWallet();
        this.loadTransactions(1);
        this.walletService.getIban().subscribe({
            next: (res) => {
                this.ibanForm.setValue({ iban: res.iban });
                this.loadingIban = false;
            },
            error: () => {
                this.loadingIban = false;
            },
        });
    }

    get canWithdraw(): boolean {
        return (
            !!this.user &&
            !this.user.team &&
            this.user.status === 'validated' &&
            !!this.ibanForm.value.iban &&
            this.wallet.balance.greaterThanOrEqualTo(1)
        );
    }

    loadCards() {
        this.cardService.list().subscribe({
            next: (cards) => {
                this.cards = cards;
            },
            error: (err) => {
                console.log(err);
            },
        });
    }

    loadWallet() {
        this.loadingWallet = true;
        this.walletService.getBalance().subscribe({
            next: (wallet) => {
                this.wallet = wallet;
                this.loadingWallet = false;
            },
            error: () => {
                this.loadingWallet = false;
            },
        });
    }

    loadTransactions(page: number) {
        this.loadingTransactions = true;
        this.walletService.getTransactions(page, this.transactions.perPage).subscribe({
            next: (result) => {
                this.transactions = result;
                this.loadingTransactions = false;
            },
            error: () => {
                this.loadingTransactions = false;
            },
        });
    }

    pageChanged(page: number) {
        this.loadTransactions(page);
    }

    setWithdrawAll() {
        this.withdrawAmount = this.wallet.balance.toFixed(2);
    }

    async requestWithdraw() {
        if (!this.canWithdraw) {
            return;
        }

        let amount: Decimal;
        try {
            amount = new Decimal(this.withdrawAmount || 0);
        } catch {
            this.toastService.error(_('Invalid amount'));
            return;
        }

        if (amount.lessThan(1)) {
            this.toastService.error(_('Minimum withdraw amount is 1'));
            return;
        }

        if (amount.greaterThan(this.wallet.balance)) {
            this.toastService.error(_('Insufficient balance'));
            return;
        }

        const confirmed = await AlertUtils.askConfirmation(_('Confirm withdraw request?'));
        if (!confirmed) {
            return;
        }

        await LoadingUtils.show();
        this.walletService.withdraw(amount.toFixed(2)).subscribe({
            next: () => {
                LoadingUtils.hide();
                this.toastService.success(_('Withdraw request submitted'));
                this.withdrawAmount = '';
                this.loadWallet();
                this.loadTransactions(1);
            },
            error: (err) => {
                LoadingUtils.hide();
                this.toastService.error(err?.error?.message || _('An error occurred'));
            },
        });
    }

    async deleteCard(card: Card) {
        await LoadingUtils.show();
        this.cardService.deleteCard(card).subscribe({
            next: () => {
                LoadingUtils.hide();
                this.loadCards();
            },
            error: (err) => {
                console.log(err);
            },
        });
    }

    async updateIban() {
        if (this.ibanForm.invalid) {
            this.ibanForm.markAllAsTouched();
            return;
        }

        const confirmed = await this.validationScopeWarningService.confirmIfNeeded();
        if (!confirmed) {
            return;
        }

        this.loadingIban = true;
        this.walletService.changeIban(this.ibanForm.value.iban).subscribe({
            next: () => {
                this.loadingIban = false;
                this.toastService.success(_('Changes saved'));
            },
            error: (err) => {
                console.log(err);
                this.loadingIban = false;
                this.toastService.error(_('An error occurred'));
            },
        });
    }
}
